import React, { useEffect, useRef } from "react"
import API from "../../api"
import { connect } from "react-redux"
import { mapDispatchProps, mapStateToProps } from "../../store/store"
import { isEmpty } from "../../helpers/object"
import * as HELPER_VERSION_CHECKER from "../../helpers/versionChecker"
import { useQueryParams } from "../../hooks/useQueryParams"
import { useLocation, useNavigate } from "react-router-dom"
import { ROUTES } from "../../routes"

import "./styles.css"
import USER from "@constants/user"
import BRAND from "@constants/brand"
import { batch } from "react-redux"
import { debounce } from "lodash"
import { APP_LOCALE, getLanguage } from "@languages/index"
import OverlayMatchRatingSettingWithSteps from "components/overlayMatchRatingSettingWithSteps"

interface Props {
  auth?: any
  user?: any
  setAuth?: any

  preferences?: any
  modal?: any
  sheet?: any
  popup?: any
  brand?: any
  settings?: any
  pathCaches?: any

  setPreferences?: (params: any) => void
  setBrand?: (params: any) => void

  showModal?: (params?: any) => void
  setModal?: (params: any) => void
  closeModal?: () => void

  showSheet?: (params: any) => void
  closeSheet?: () => void

  showPopup?: (params: any) => void
  closePopup?: () => void

  setSheet?: (params: any) => void
  setPopup?: (params: any) => void

  showOverlays?: (params?: any) => void
  closeOverlays?: () => void

  showNotification?: (params: any) => void
  setNotification?: (params: any) => void

  setSettings?: (params: any) => void

  clearPathCaches?: (params: any) => void
}

const RootEventListener = (props: Props) => {
  const NEEDLESS_AUTH_PATHS = [ROUTES.RENTAL_AMBASSADOR_PREVIEW_INDEX.PATH]

  const queryParams = useQueryParams()
  const location = useLocation()
  const navigate = useNavigate()

  const isLectureOnlyAccount = useRef(BRAND.isLectureOnlyBrand(props.brand.id))
  const isBeautyCategoryBrand = useRef(props.brand?.isCategoryBeauty)

  useEffect(() => {
    initialize(queryParams)
  }, [queryParams])

  useEffect(() => {
    if (props.auth) {
      onAuthUpdated()
    }
  }, [props.auth])

  useEffect(() => {
    // 공통
    props.setSettings({ 
      currencyText: getBrandCurrencyText(),
      influencerCurrencyText: getInfluencerCurrencyText()
    })

    let pluginKey: string | null = null;
    if (props.auth?.token != null && !isEmpty(props.brand)) {
      if (props.brand?.isCategoryBeauty) {
        pluginKey = process.env.CHANNEL_IO_PLUGIN_KEY_GLOVV_KR
      }
    }

    if (pluginKey == null || pluginKey.trim() === "") {
      if (typeof (window as any).ChannelIO !== "undefined") {
        hideChannelIO()
      }
    } else if (false) { // CS 과부하로 임시 비활성화 (250827)
      // ChannelIO 스크립트
      if (typeof (window as any).ChannelIO === "undefined") {
        const initializeChannelIO = () => {
          ;(function () {
            var w = window as any
            if (w.ChannelIO) {
              return w.console.error("ChannelIO script included twice.")
            }
            var ch: any = function () {
              ch.c(arguments)
            }
            ch.q = []
            ch.c = function (args: any) {
              ch.q.push(args)
            }
            w.ChannelIO = ch
            function l() {
              if (w.ChannelIOInitialized) {
                return
              }
              w.ChannelIOInitialized = true
              var s = document.createElement("script")
              s.type = "text/javascript"
              s.async = true
              s.src = "https://cdn.channel.io/plugin/ch-plugin-web.js"
              var x = document.getElementsByTagName("script")[0]
              if (x.parentNode) {
                x.parentNode.insertBefore(s, x)
              }
            }
            if (document.readyState === "complete") {
              l()
            } else {
              w.addEventListener("DOMContentLoaded", l)
              w.addEventListener("load", l)
            }
          })()
        }
        initializeChannelIO()
        ;(window as any).ChannelIO("boot", {
          pluginKey: pluginKey,
          profile: {
            name: props.brand.name,
            token: props.auth.token,
          },
        })
      } else {
        // ChannelIO가 이미 초기화된 경우, 프로필 업데이트
        ;(window as any).ChannelIO("updateUser", {
          name: props.brand.name,
          token: props.auth.token,
        })
      }
    }
  }, [props.brand, props.auth.token])

  useEffect(() => {
    props.clearPathCaches({ pathname: location.pathname })
    fetchServerSettings()
  }, [location.pathname])

  useEffect(() => {
    fetchServerSettings()
    HELPER_VERSION_CHECKER.initVersionChecker();

    return () => {
      HELPER_VERSION_CHECKER.cancelVersionChecker();
    }
  }, []);

  // useEffect(() => {
  //   console.log("locale - updated", props.preferences.locale)
  //   const originalBrandToken = props.auth.token
  //   props.setAuth({ token: null })
  //   setTimeout(() => {
  //     props.setAuth({ token: originalBrandToken })
  //   }, 300)
  // }, [props.preferences.locale])

  
  // @todo header, rootEventListener, sidebar. 필요하면 통합
  const getBrandCurrencyText = () => {
    const serverLocale = process.env.LOCALE
    const userLocale = getLanguage() ?? props?.preferences?.locale
    const isJpy = props.brand.currency === "JPY"
    const isKrw = props.brand.currency === "KRW"
    const provider = isJpy ? "JPY" : "KRW"
  
    const currencyMap = {
      JPY: {
        ko: "엔",
        ja: "円",
        default: { ko: "엔", ja: "円" },
      },
      KRW: {
        ko: "원",
        ja: "ウォン",
        default: { ko: "원", ja: "ウォン" },
      },
    }

    if (isJpy || isKrw) {
      return currencyMap[provider][userLocale] || currencyMap[provider].default[serverLocale]
    }

    return currencyMap[provider].default[serverLocale] || currencyMap[provider].default[props.preferences.locale]
  }

  const getInfluencerCurrencyText = () => {
    const serverLocale = process.env.LOCALE
    const userLocale = getLanguage() ?? props?.preferences?.locale
    const isJpy = serverLocale === "ja"
    const isKrw = serverLocale === "ko"
    const influencer = isJpy ? "JPY" : "KRW"
  
    const currencyMap = {
      JPY: {
        ko: "엔",
        ja: "円",
        default: { ko: "엔", ja: "円" },
      },
      KRW: {
        ko: "원",
        ja: "ウォン",
        default: { ko: "원", ja: "ウォン" },
      },
    }
    if (isJpy || isKrw) {
      return currencyMap[influencer][userLocale] || currencyMap[influencer].default[serverLocale]
    }
    return currencyMap[influencer].default[props.preferences.locale] || currencyMap[influencer].default[serverLocale]
  }

  const fetchServerSettings = async () => {
    try {
      const response = await API.Settings.index({
        brandToken: props.auth.token,
      })

      props.setSettings({ ...response.data.settings})
    } catch (error) {
      console.log(error)
    }
  }

  const onAuthUpdated = () => {
    debounce(() => {
      API.Auth.show({ brandToken: props.auth.token })
        .then((response) => {
          batch(() => {
            props.setBrand(response.data.brand)
            props.setPreferences({
              isAuthenticated: true,
              brandName: response.data.brand.name,
            })
          }, [])

          handleBrandInitialize(response.data.brand)
        })
        .catch((error) => {
          conditionalNavigate(location.pathname, ROUTES.AUTH_INDEX.PATH)
        })

      if (isEmpty(props.auth)) {
        conditionalNavigate(location.pathname, ROUTES.AUTH_INDEX.PATH)
        return
      }
    }, 500)
  }

  const updateBrandStateRefs = (brand) => {
    if (brand == null) {
      return
    }

    isLectureOnlyAccount.current = BRAND.isLectureOnlyBrand(brand.id)
    isBeautyCategoryBrand.current = brand?.isCategoryBeauty
  }

  const initialize = async (queryParams: any) => {
    let isTokenAssigned = false

    const isWebView = navigator.userAgent.match(/OneulookBrandWebView/) ?? false
    props.setPreferences({ isWebView })

    const hasQueryParamsToken = queryParams && queryParams.brand_token
    const hasPropsToken = !isEmpty(props.auth) && props.auth.token

    if (hasQueryParamsToken || hasPropsToken) {
      let token = ""
      if (hasQueryParamsToken) token = queryParams.brand_token
      if (hasPropsToken) token = props.auth.token

      props.setAuth({ token: token })
      isTokenAssigned = true

      const _brand = await fetchBrand({ token: token })
      if (_brand) {
        handleBrandInitialize(_brand)
      }
    } else {
      conditionalNavigate(location.pathname, ROUTES.AUTH_INDEX.PATH)
    }
  }

  const handleBrandInitialize = (brand) => {
    updateBrandStateRefs(brand)
    if (!brand.hasExperiencedMatchRatingCreate && !isBeautyCategoryBrand.current) {
      props.showOverlays({
        preventClose: false,
        content: () => {
          return <OverlayMatchRatingSettingWithSteps {...props} hasCloseButton={false} />
        },
      })
    }

    if (location.pathname != ROUTES.AUTH_INDEX.PATH) {
      return false
    }

    if (isLectureOnlyAccount.current) {
      navigate(ROUTES.LECTURE_INDEX.PATH)
      return false
    }

    if (brand.productsInRentableCount == 0 && (brand.hasExperiencedCampaign || brand.isCategoryBeauty)) {
      navigate(ROUTES.CAMPAIGN_INDEX.PATH, { replace: true })
      return false
    }

    const isAmbassadorEnabled =
      brand.rentableUserTypeFl.includes(USER.TYPE.RENTAL_AMBASSADOR) && brand.isAmbassadorServiceEnabled
    if (brand.businessType == BRAND.BUSINESS_TYPE.NORMAL && isAmbassadorEnabled) {
      navigate(ROUTES.RENTAL_AMBASSADOR_INDEX.PATH, {
        state: {
          scrollTo: "applicantsSectionStartRef",
        },
      })
      return false
    }

    if (
      brand.hasExperiencedInfoUpdate == false ||
      brand.hasExperiencedAlarmUpdate == false ||
      brand.hasExperiencedPasswordUpdate == false
    ) {
      navigate(ROUTES.HELP_MISSIONS.PATH)
      return false
    }
  }

  const conditionalNavigate = (currentPathname, targetPathname) => {
    if (NEEDLESS_AUTH_PATHS.indexOf(currentPathname) >= 0) {
      return
    }

    props.setPreferences({
      prevUrl: currentPathname,
    })
    return navigate(targetPathname)
  }

  const showChannelIO = () => {
    ;(window as any).ChannelIO("showChannelButton")
  }

  const hideChannelIO = () => {
    ;(window as any).ChannelIO("hideChannelButton")
  }

  const fetchBrand = async (config?: any) => {
    try {
      const token = config?.token ? config.token : props.auth.token
      const response = await API.Brand.show({ brandToken: token })
      const _brand = response.data.brand
      props.setBrand(_brand)
      return _brand
    } catch (error) {}
  }

  return <></>
}

export default connect(mapStateToProps, mapDispatchProps)(RootEventListener)
